import type { Account, SavingGoal, GoalSpending } from "../types";
import { roundMoney } from "./money-math";
import { getGoalMonths, goalContributionMonth, goalTotalSpent, goalIsCompleted } from "./goals";

export interface GoalAccountHint {
  cushionAccountId?: string | null;
  profile?: string | null;
  currency: string;
  schedules?: { accountId: string; active?: boolean }[];
  contributions?: { accountId?: string | null }[];
}

/** Resolves the linked or primary account ID for a goal (including "Auto (Primary)" fallback). */
export function resolveGoalCushionAccountId(
  goal: GoalAccountHint,
  accounts?: Account[],
): string | null {
  if (goal.cushionAccountId) return goal.cushionAccountId;
  const schedules = goal.schedules ?? [];
  const activeSchedule = schedules.find((s) => s.active !== false && s.accountId);
  if (activeSchedule) return activeSchedule.accountId;
  if (schedules.length > 0 && schedules[0].accountId) return schedules[0].accountId;

  const contributions = goal.contributions ?? [];
  for (let i = contributions.length - 1; i >= 0; i--) {
    const accId = contributions[i].accountId;
    if (accId) return accId;
  }

  if (accounts && accounts.length > 0) {
    const isNonCard = (a: Account) => {
      const t = a.type.toLowerCase();
      return !t.includes("credit") && !t.includes("card");
    };
    const activeNonCard = accounts.filter((a) => a.active !== false && isNonCard(a));
    const pool = activeNonCard.length > 0 ? activeNonCard : accounts.filter((a) => a.active !== false);

    if (goal.profile) {
      const ownerBank = pool.find(
        (a) => a.owner === goal.profile && a.currency === goal.currency && a.type.toLowerCase() !== "cash",
      );
      if (ownerBank) return ownerBank.id;
      const ownerCurr = pool.find((a) => a.owner === goal.profile && a.currency === goal.currency);
      if (ownerCurr) return ownerCurr.id;
    }

    const bankMatch = pool.find((a) => a.currency === goal.currency && a.type.toLowerCase() !== "cash");
    if (bankMatch) return bankMatch.id;
    const currMatch = pool.find((a) => a.currency === goal.currency);
    if (currMatch) return currMatch.id;

    if (goal.profile) {
      const ownerAny = pool.find((a) => a.owner === goal.profile);
      if (ownerAny) return ownerAny.id;
    }
    if (pool.length > 0) return pool[0].id;
  }

  return null;
}

/** Net amount currently reserved for a single goal within a specific account as of endMonth. */
export function goalReservedForAccount(
  goal: SavingGoal,
  accountId: string,
  asOfMonth?: string,
  transactions?: GoalSpending[],
  accounts?: Account[],
): number {
  if (!accountId || (goal.status !== "active" && goal.status !== "achieved")) return 0;
  const endMonth = asOfMonth ?? new Date().toISOString().slice(0, 7);
  if (goalIsCompleted(goal, endMonth)) return 0;
  const singleAccountOverride =
    goal.cushionAccountId && goal.schedules.length === 0
      ? goal.cushionAccountId
      : null;
  const fallbackAccountId =
    goal.cushionAccountId ||
    goal.schedules.find((s) => s.active && s.accountId)?.accountId ||
    goal.contributions.find((c) => c.accountId)?.accountId ||
    (accounts ? resolveGoalCushionAccountId(goal, accounts) : null) ||
    null;
  let reserved = 0;

  for (const c of goal.contributions) {
    if (c.deletedAt) continue;
    const contribAccountId = singleAccountOverride || c.accountId || fallbackAccountId;
    if (contribAccountId !== accountId) continue;
    const contribMonth = goalContributionMonth(c);
    if (asOfMonth && contribMonth && contribMonth > endMonth) continue;
    if (c.kind === "release") {
      reserved = roundMoney(reserved - c.amount);
    } else if (c.kind === "ad_hoc") {
      reserved = roundMoney(reserved + c.amount);
    }
  }

  if (goal.schedules.length > 0 && goal.startDate) {
    for (const m of getGoalMonths(goal.startDate, endMonth)) {
      const monthContribs = goal.contributions.filter((c) => c.month === m);
      if (monthContribs.some((c) => c.kind === "skip" && !c.deletedAt)) continue;

      const explicitScheduled = monthContribs.filter(
        (c) => c.kind === "scheduled" && (c.accountId || fallbackAccountId) === accountId,
      );
      if (monthContribs.some((c) => c.kind === "scheduled")) {
        for (const c of explicitScheduled) {
          if (!c.deletedAt) reserved = roundMoney(reserved + c.amount);
        }
      } else {
        for (const s of goal.schedules) {
          if (s.active && s.accountId === accountId && s.monthlyAmount > 0) {
            reserved = roundMoney(reserved + s.monthlyAmount);
          }
        }
      }
    }
  } else {
    for (const c of goal.contributions) {
      if (c.deletedAt) continue;
      const contribAccountId = singleAccountOverride || c.accountId || fallbackAccountId;
      if (contribAccountId !== accountId || c.kind !== "scheduled") continue;
      const contribMonth = goalContributionMonth(c);
      if (asOfMonth && contribMonth && contribMonth > endMonth) continue;
      reserved = roundMoney(reserved + c.amount);
    }
  }

  if (transactions && reserved > 0) {
    for (const t of transactions) {
      if (
        t.goalId === goal.id &&
        (!t.currency || t.currency === goal.currency) &&
        (t.accountId === accountId || singleAccountOverride === accountId) &&
        t.type.toLowerCase() === "expense" &&
        (!asOfMonth || t.date.slice(0, 7) <= endMonth)
      ) {
        reserved = Math.max(0, roundMoney(reserved - Math.abs(t.amount)));
      }
    }
  }

  return Math.max(0, roundMoney(reserved));
}

export interface AccountGoalReserveItem {
  goal: SavingGoal;
  reserved: number;
  cushionAmount: number;
  monthlyCommitment: number;
  cushionStatus: GoalCushionStatus | null;
}

/**
 * Returns the list of active (or unspent achieved) goals tied to or funded by a specific account,
 * along with each goal's reserved balance, safety cushion, and monthly commitment on that account.
 */
export function accountGoalBreakdown(
  goals: SavingGoal[] | undefined,
  accountId: string,
  asOfMonth?: string,
  transactions?: GoalSpending[],
  accounts?: Account[],
): AccountGoalReserveItem[] {
  if (!goals || !accountId) return [];
  const items: AccountGoalReserveItem[] = [];

  for (const g of goals) {
    if (g.status !== "active" && g.status !== "achieved") continue;
    if (goalIsCompleted(g, asOfMonth)) continue;

    const hasStarted = !asOfMonth || g.startDate.slice(0, 7) <= asOfMonth;
    const reserved = goalReservedForAccount(g, accountId, asOfMonth, transactions, accounts);
    const totalSpent = transactions ? goalTotalSpent(g, transactions, asOfMonth) : 0;
    if (g.status === "achieved" && totalSpent >= g.targetAmount && reserved <= 0) continue;

    const resolvedCushionAccId = resolveGoalCushionAccountId(g, accounts);
    const hasCushionOnAccount =
      hasStarted &&
      g.cushionAmount != null &&
      g.cushionAmount > 0 &&
      resolvedCushionAccId === accountId &&
      totalSpent < g.targetAmount;
    const cushionAmount = hasCushionOnAccount ? roundMoney(g.cushionAmount!) : 0;
    const cushionStatus = hasCushionOnAccount ? goalCushionStatus(g, accounts, totalSpent) : null;

    const monthlyCommitment = hasStarted ? roundMoney(
      g.schedules
        .filter((s) => s.active && s.accountId === accountId && s.monthlyAmount > 0)
        .reduce((sum, s) => sum + s.monthlyAmount, 0),
    ) : 0;

    const hasExplicitLink = (hasStarted && g.cushionAccountId === accountId) || monthlyCommitment > 0;

    if (reserved > 0 || cushionAmount > 0 || hasExplicitLink) {
      items.push({
        goal: g,
        reserved,
        cushionAmount,
        monthlyCommitment,
        cushionStatus,
      });
    }
  }

  return items;
}

/** Maps each account ID to the total amount currently reserved across all active/unspent goals. */
export function accountGoalReserves(
  goals: SavingGoal[] | undefined,
  asOfMonth?: string,
  transactions?: GoalSpending[],
  accounts?: Account[],
): Map<string, number> {
  const reserves = new Map<string, number>();
  if (!goals) return reserves;

  for (const g of goals) {
    if (g.status !== "active" && g.status !== "achieved") continue;
    const accountIds = new Set<string>();
    if (g.cushionAccountId) accountIds.add(g.cushionAccountId);
    for (const c of g.contributions) if (c.accountId) accountIds.add(c.accountId);
    for (const s of g.schedules) if (s.accountId) accountIds.add(s.accountId);
    if (accounts) {
      const resolved = resolveGoalCushionAccountId(g, accounts);
      if (resolved) accountIds.add(resolved);
    }

    for (const accId of accountIds) {
      const goalReserved = goalReservedForAccount(g, accId, asOfMonth, transactions, accounts);
      if (goalReserved > 0) {
        reserves.set(accId, roundMoney((reserves.get(accId) ?? 0) + goalReserved));
      }
    }
  }

  return reserves;
}

export interface GoalCushionStatus {
  cushionAmount: number;
  cushionAccountId: string | null;
  accountName: string | null;
  accountBalance: number | null;
  postPurchaseBalance: number | null;
  shortfall: number;
  isSafeToSpend: boolean;
  minAccountBalanceRequired: number;
}

/**
 * Calculates safety cushion status for a goal against its linked or primary account balance.
 * Returns null if the goal has no cushion configured (cushionAmount is null/undefined or <= 0).
 */
export function goalCushionStatus(
  goal: SavingGoal,
  accounts: Account[] | undefined,
  spentOrTransactions?: number | GoalSpending[],
): GoalCushionStatus | null {
  if (goal.cushionAmount == null || goal.cushionAmount <= 0) {
    return null;
  }

  const spent = Array.isArray(spentOrTransactions)
    ? goalTotalSpent(goal, spentOrTransactions)
    : (spentOrTransactions ?? 0);

  const cushionAmount = roundMoney(goal.cushionAmount);
  const cushionAccountId = resolveGoalCushionAccountId(goal, accounts);
  const account = cushionAccountId && accounts ? accounts.find((a) => a.id === cushionAccountId) : undefined;

  const remainingGoalCost = Math.max(0, roundMoney(goal.targetAmount - spent));
  const minAccountBalanceRequired = roundMoney(cushionAmount + remainingGoalCost);

  if (!account) {
    return {
      cushionAmount,
      cushionAccountId,
      accountName: null,
      accountBalance: null,
      postPurchaseBalance: null,
      shortfall: 0,
      isSafeToSpend: false,
      minAccountBalanceRequired,
    };
  }

  const accountBalance = roundMoney(account.balance);
  const postPurchaseBalance = roundMoney(accountBalance - remainingGoalCost);
  const shortfall = Math.max(0, roundMoney(cushionAmount - postPurchaseBalance));
  const isSafeToSpend = shortfall <= 0;

  return {
    cushionAmount,
    cushionAccountId,
    accountName: account.name,
    accountBalance,
    postPurchaseBalance,
    shortfall,
    isSafeToSpend,
    minAccountBalanceRequired,
  };
}
